import { formatUtcDateWithOffset } from 'util/utils';

import { EPDClient, FileParameter, SendForPublicationEditorialModel } from './EpdClient';
import { DepublishReasonType } from 'types/types';

const client = new EPDClient(process.env.REACT_APP_API_URL);

class EpdStateService {
  static delete = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }

    const result = await client.delete(epdId);
    return result;
  };

  static cancelUpdate = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }

    const result = await client.cancelUpdate(epdId);
    return result;
  };

  static startVerification = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.startVerification(epdId);
    return result;
  };

  static cancelVerification = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.cancelVerification(epdId);
    return result;
  };

  static publish = async (epdVersionId: string | undefined, epdId: string | undefined) => {
    if (!epdId) {
      return null;
    }
    if (!epdVersionId) {
      return null;
    }
    const result = await client.publish(epdVersionId, epdId);
    return result;
  };

  static declinePublication = async (epdId: string | undefined, declineReason: string) => {
    if (!epdId) {
      return null;
    }
    return await client.declinePublication(epdId, declineReason);
  };

  static depublish = async (
    epdId: string | undefined,
    depublicationReason?: string,
    depublishType?: DepublishReasonType | null
  ) => {
    if (!epdId) {
      return null;
    }
    const result = await client.depublish(epdId, depublicationReason, depublishType);
    return result;
  };

  static sendForPublication = async (epdId: string | undefined) => {
    if (!epdId) {
      return null;
    }
    const result = await client.sendEpdForPublication(epdId);
    return result;
  };

  static cancelRegistration = async (epdId: string | undefined) => {
    if (!epdId) {
      return null;
    }
    const result = await client.cancelRegistration(epdId);
    return result;
  };

  static approve = async (epdId: string | undefined) => {
    return await EpdStateService.approveVerification(epdId, formatUtcDateWithOffset(new Date()));
  };

  static approveVerification = async (epdId: string | undefined, currentDateTimeWithTimezone: string) => {
    if (!epdId) {
      return;
    }
    const result = await client.approve(epdId, currentDateTimeWithTimezone);
    return result;
  };

  static declineVerification = async (epdId: string | undefined, reason: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.declineVerification(epdId, reason);
    return result;
  };

  static deregister = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }

    const result = await client.deregister(epdId);
    return result;
  };

  static cancelDeregisteration = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.cancelDeregistration(epdId);
    return result;
  };

  static unlock = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.unlock(epdId);
    return result;
  };

  static getVersions = async (epdId: string) => {
    if (!epdId) {
      return;
    }
    const result = await client.getVersions(epdId);
    return result;
  };

  static sendForPublicationEditorial = async (epdId: string, notes: string | undefined) => {
    if (!epdId) {
      return;
    }
    const registrationEditor: SendForPublicationEditorialModel = { epdId, notes };
    const result = await client.sendEpdForPublicationEditorial(epdId, registrationEditor);
    return result;
  };

  static licenseeAdminVerificationApprove = async (epdId: string) => {
    if (!epdId) {
      return;
    }
    const result = await client.licenseeAdminVerificationApprove(epdId);
    return result;
  };
  static licenseeAdminVerificationDecline = async (epdId: string | undefined, declineReason: string) => {
    if (!epdId) {
      return null;
    }
    const result = await client.licenseeAdminVerificationDecline(epdId, declineReason);
    return result;
  };

  static cancelDeclineVerification = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.cancelDeclineVerification(epdId);
    return result;
  };

  static cancelApproveVerification = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.cancelApproveVerification(epdId);
    return result;
  };

  static getEPDVerificationStatesModel = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.getEPDVerificationStatesModel(epdId);
    return result;
  };
  static getAllEPDCopyGroupFiles = async (epdId: string | undefined) => {
    if (!epdId) {
      return;
    }
    const result = await client.getAllEPDCopyGroupFiles(epdId);
    return result;
  };
  static removeCopyGroupFile = async (copyGroupId: string | undefined, fileId: string | undefined) => {
    if (!copyGroupId || !fileId) {
      return;
    }
    const result = await client.removeCopyGroupFile(copyGroupId, fileId);
    return result;
  };
  static addCopyGroupFile = async (
    copyGroupId: string | undefined,
    propertyName: string | undefined,
    fileName: string,
    newValue: any
  ) => {
    if (!copyGroupId || !propertyName || !newValue || !fileName) {
      return;
    }
    const addFile: FileParameter = { data: newValue, fileName: fileName };
    const result = await client.addCopyGroupFile(copyGroupId, propertyName, addFile);
    return result;
  };
}

export default EpdStateService;
